import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../utils/axios";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";

const API = {
  tickets: "/service-desk",
  departments: "/departments",
  users: "/admin/users",
};

const STATUSES = ["Open", "In Progress", "Pending", "Resolved", "Closed"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];

const CATEGORIES = [
  "Hardware",
  "Software",
  "Software Licence",
  "Network",
  "Access",
  "Account",
  "Maintenance",
  "Other",
];

const EMPTY_FORM = {
  subject: "",
  description: "",
  category: "Hardware",
  priority: "Medium",
  status: "Open",
  department_id: "",
  requester_id: "",
  assigned_to: "",
};

function responseList(response) {
  const body = response?.data;

  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.data?.data)) return body.data.data;

  return [];
}

function responseRecord(response) {
  const body = response?.data;
  return body?.data?.data || body?.data || body?.ticket || body;
}

function errorMessage(error) {
  const body = error?.response?.data;

  if (body?.errors) {
    return Object.values(body.errors).flat().join(" ");
  }

  return body?.message || error?.message || "An unexpected error occurred.";
}

function normalizeTicket(ticket, users = [], departments = []) {
  const requester = users.find(
    (user) => String(user.id) === String(ticket.requester_id),
  );

  const department = departments.find(
    (item) => String(item.id) === String(ticket.department_id),
  );

  const assignee = users.find(
    (user) => String(user.id) === String(ticket.assigned_to),
  );

  return {
    ...ticket,
    subject: ticket.subject || "",
    description: ticket.description || "",
    category: ticket.category || "Other",
    priority: ticket.priority || "Medium",
    status: ticket.status || "Open",
    requesterName:
      ticket.requester?.name ||
      ticket.requester_name ||
      requester?.name ||
      `User #${ticket.requester_id ?? "—"}`,
    departmentName:
      ticket.department?.name ||
      ticket.department_name ||
      department?.name ||
      (ticket.department_id
        ? `Department #${ticket.department_id}`
        : "Not assigned"),
    assigneeName:
      ticket.assignee?.name ||
      ticket.assigned_to_name ||
      assignee?.name ||
      (ticket.assigned_to ? `User #${ticket.assigned_to}` : "Unassigned"),
  };
}

function statusClass(status) {
  switch (status) {
    case "Resolved":
    case "Closed":
      return "bg-success/10 text-success border-success/30";
    case "In Progress":
      return "bg-primary/10 text-primary border-primary/30";
    case "Pending":
      return "bg-warning/10 text-warning border-warning/30";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

function priorityClass(priority) {
  switch (priority) {
    case "Critical":
      return "bg-error/10 text-error border-error/30";
    case "High":
      return "bg-warning/10 text-warning border-warning/30";
    case "Medium":
      return "bg-primary/10 text-primary border-primary/30";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString();
}

function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString();
}

const ServiceDesk = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [activeTicket, setActiveTicket] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchReferenceData = useCallback(async () => {
    const results = await Promise.allSettled([
      axiosInstance.get(API.departments, {
        params: { status: "active", per_page: 100 },
      }),
      axiosInstance.get(API.users),
    ]);

    if (results[0].status === "fulfilled") {
      setDepartments(responseList(results[0].value));
    }

    if (results[1].status === "fulfilled") {
      const records = responseList(results[1].value);

      setUsers(
        records.filter((user) => {
          const status = String(user.status || "").toLowerCase();
          return !status || status === "active";
        }),
      );
    }

    const failures = results
      .filter((result) => result.status === "rejected")
      .map((result) => errorMessage(result.reason));

    if (failures.length) {
      setError((previous) => [previous, ...failures].filter(Boolean).join(" "));
    }
  }, []);

  const fetchTickets = useCallback(
    async (page = currentPage, searchTerm = search) => {
      setLoading(true);
      setError("");

      try {
        const params = {
          page,
          per_page: 100,
        };

        if (searchTerm.trim()) {
          params.search = searchTerm.trim();
        }

        // The backend supports only its defined status and priority values.
        if (activeTab !== "All") params.status = activeTab;
        if (priorityFilter !== "All") params.priority = priorityFilter;

        const response = await axiosInstance.get(API.tickets, { params });
        const body = response?.data || {};

        const records = responseList(response);

        setTickets(
          records.map((ticket) =>
            normalizeTicket(ticket, users, departments),
          ),
        );

        setPagination(
          body.pagination || {
            current_page: page,
            last_page: 1,
            per_page: records.length,
            total: records.length,
          },
        );
      } catch (requestError) {
        setError(errorMessage(requestError));
        setTickets([]);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, search, activeTab, priorityFilter, users, departments],
  );

  const refreshAll = useCallback(async () => {
    await fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    fetchTickets(currentPage, search);
  }, [fetchTickets, currentPage, search, activeTab, priorityFilter]);

  useEffect(() => {
    if (!successMessage) return undefined;

    const timer = window.setTimeout(() => setSuccessMessage(""), 4000);
    return () => window.clearTimeout(timer);
  }, [successMessage]);

  const stats = useMemo(() => {
    const counts = {
      total: pagination.total,
      open: 0,
      progress: 0,
      pending: 0,
      resolved: 0,
      closed: 0,
    };

    tickets.forEach((ticket) => {
      if (ticket.status === "Open") counts.open++;
      if (ticket.status === "In Progress") counts.progress++;
      if (ticket.status === "Pending") counts.pending++;
      if (ticket.status === "Resolved") counts.resolved++;
      if (ticket.status === "Closed") counts.closed++;
    });

    return counts;
  }, [tickets, pagination.total]);

  const categories = useMemo(
    () => [
      ...new Set([
        ...CATEGORIES,
        ...tickets.map((ticket) => ticket.category).filter(Boolean),
      ]),
    ],
    [tickets],
  );

  const filteredTickets = useMemo(() => {
    return tickets.filter((ticket) => {
      const matchesPriority =
        priorityFilter === "All" || ticket.priority === priorityFilter;

      const matchesCategory =
        categoryFilter === "All" || ticket.category === categoryFilter;

      return matchesPriority && matchesCategory;
    });
  }, [tickets, priorityFilter, categoryFilter]);

  const updateField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const openCreateModal = () => {
    setEditingTicket(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (ticket) => {
    setEditingTicket(ticket);
    setForm({
      subject: ticket.subject || "",
      description: ticket.description || "",
      category: ticket.category || "Hardware",
      priority: ticket.priority || "Medium",
      status: ticket.status || "Open",
      department_id: ticket.department_id
        ? String(ticket.department_id)
        : "",
      requester_id: ticket.requester_id
        ? String(ticket.requester_id)
        : "",
      assigned_to: ticket.assigned_to ? String(ticket.assigned_to) : "",
    });
    setFormError("");
    setActiveTicket(null);
    setShowModal(true);
  };

  const saveTicket = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!form.subject.trim()) {
      setFormError("Please enter a ticket subject.");
      return;
    }

    if (!form.requester_id) {
      setFormError("Please select a requester.");
      return;
    }

    if (!form.priority) {
      setFormError("Please select a priority.");
      return;
    }

    const payload = {
      subject: form.subject.trim(),
      description: form.description,
      category: form.category || null,
      priority: form.priority,
      department_id: form.department_id
        ? Number(form.department_id)
        : null,
      requester_id: Number(form.requester_id),
      assigned_to: form.assigned_to ? Number(form.assigned_to) : null,
    };

    if (editingTicket) {
      payload.status = form.status;
    }

    setSaving(true);

    try {
      let response;

      if (editingTicket) {
        response = await axiosInstance.patch(
          `${API.tickets}/${editingTicket.id}`,
          payload,
        );
      } else {
        response = await axiosInstance.post(API.tickets, payload);
      }

      const savedRecord = responseRecord(response);

      setShowModal(false);
      setEditingTicket(null);
      setSuccessMessage(
        response?.data?.message ||
          (editingTicket
            ? "Service ticket updated successfully."
            : "Service ticket created successfully."),
      );

      if (savedRecord?.id) {
        setActiveTicket(
          normalizeTicket(savedRecord, users, departments),
        );
      }

      await fetchTickets(currentPage, search);
    } catch (requestError) {
      setFormError(errorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (ticket, newStatus) => {
    setSaving(true);
    setError("");

    try {
      const response = await axiosInstance.patch(
        `${API.tickets}/${ticket.id}`,
        { status: newStatus },
      );

      const updated = responseRecord(response);

      setActiveTicket(
        normalizeTicket(updated, users, departments),
      );

      setSuccessMessage(
        response?.data?.message || `Status changed to ${newStatus}.`,
      );

      await fetchTickets(currentPage, search);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const deleteTicket = async () => {
    if (!deleteTarget) return;

    setSaving(true);
    setError("");

    try {
      const response = await axiosInstance.delete(
        `${API.tickets}/${deleteTarget.id}`,
      );

      setDeleteTarget(null);
      setActiveTicket(null);

      setSuccessMessage(
        response?.data?.message || "Service ticket deleted successfully.",
      );

      await fetchTickets(currentPage, search);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const exportCSV = () => {
    const rows = [
      [
        "Ticket Number",
        "Subject",
        "Description",
        "Category",
        "Priority",
        "Status",
        "Requester",
        "Department",
        "Assignee",
        "Created At",
        "Resolved At",
      ],
      ...filteredTickets.map((ticket) => [
        ticket.ticket_number,
        ticket.subject,
        ticket.description,
        ticket.category,
        ticket.priority,
        ticket.status,
        ticket.requesterName,
        ticket.departmentName,
        ticket.assigneeName,
        ticket.created_at,
        ticket.resolved_at,
      ]),
    ];

    const csv = rows
      .map((row) =>
        row
          .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\r\n");

    const blob = new Blob(["\uFEFF", csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `service_tickets_${new Date().toISOString().slice(0, 10)}.csv`;

    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { label: "All Tickets", value: "All", icon: "Inbox" },
    { label: "Open", value: "Open", icon: "CircleDot" },
    { label: "In Progress", value: "In Progress", icon: "Clock" },
    { label: "Pending", value: "Pending", icon: "Timer" },
    { label: "Resolved", value: "Resolved", icon: "CheckCircle2" },
    { label: "Closed", value: "Closed", icon: "Archive" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="flex">
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() =>
            setSidebarCollapsed((previous) => !previous)
          }
        />

        <main
          className={`mt-16 min-w-0 flex-1 transition-layout ${
            sidebarCollapsed ? "ml-16" : "ml-72"
          }`}
        >
          <div className="p-4 md:p-8">
            <Breadcrumb />

            {successMessage && (
              <div className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-success/30 bg-success/10 p-4 text-success">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Icon name="CheckCircle2" size={18} />
                  {successMessage}
                </div>
                <button
                  type="button"
                  onClick={() => setSuccessMessage("")}
                  className="text-xs hover:underline"
                >
                  Dismiss
                </button>
              </div>
            )}

            {error && (
              <div className="mb-5 flex items-start justify-between gap-3 rounded-xl border border-error/30 bg-error/10 p-4 text-sm text-error">
                <div className="break-words">{error}</div>
                <button
                  type="button"
                  onClick={() => {
                    refreshAll();
                    fetchTickets(1, search);
                  }}
                  className="shrink-0 font-semibold underline"
                >
                  Retry
                </button>
              </div>
            )}

            <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon name="Headset" size={24} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground md:text-3xl">
                    Service Desk
                  </h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Create, assign, prioritize, and track service tickets.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={exportCSV}
                >
                  Export CSV
                </Button>
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={openCreateModal}
                >
                  New Ticket
                </Button>
              </div>
            </div>

            <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
              {[
                {
                  label: "Total Tickets",
                  value: pagination.total,
                  description: "All matching backend tickets",
                  icon: "ClipboardList",
                  color: "text-primary",
                  bg: "bg-primary/10",
                },
                {
                  label: "Open",
                  value: stats.open,
                  description: "On the current result page",
                  icon: "CircleDot",
                  color: "text-warning",
                  bg: "bg-warning/10",
                },
                {
                  label: "In Progress",
                  value: stats.progress,
                  description: "On the current result page",
                  icon: "Clock",
                  color: "text-primary",
                  bg: "bg-primary/10",
                },
                {
                  label: "Resolved",
                  value: stats.resolved,
                  description: "On the current result page",
                  icon: "CheckCircle2",
                  color: "text-success",
                  bg: "bg-success/10",
                },
              ].map((card) => (
                <div
                  key={card.label}
                  className="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        {card.label}
                      </p>
                      <p className={`mt-2 text-3xl font-bold ${card.color}`}>
                        {card.value}
                      </p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {card.description}
                      </p>
                    </div>
                    <div
                      className={`flex h-12 w-12 items-center justify-center rounded-xl ${card.bg} ${card.color}`}
                    >
                      <Icon name={card.icon} size={22} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <section className="mb-8 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
              <div className="border-b border-border p-5">
                <div className="flex flex-wrap gap-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.value}
                      type="button"
                      onClick={() => {
                        setActiveTab(tab.value);
                        setCurrentPage(1);
                      }}
                      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
                        activeTab === tab.value
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      <Icon name={tab.icon} size={15} />
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  <div className="relative min-w-0 flex-1">
                    <Icon
                      name="Search"
                      size={17}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value);
                        setCurrentPage(1);
                      }}
                      placeholder="Search ticket number, subject, description..."
                      className="w-full rounded-lg border border-border bg-background py-2.5 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>

                  <select
                    value={priorityFilter}
                    onChange={(event) => {
                      setPriorityFilter(event.target.value);
                      setCurrentPage(1);
                    }}
                    className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  >
                    <option value="All">All Priorities</option>
                    {PRIORITIES.map((priority) => (
                      <option key={priority} value={priority}>
                        {priority}
                      </option>
                    ))}
                  </select>

                  <select
                    value={categoryFilter}
                    onChange={(event) => setCategoryFilter(event.target.value)}
                    className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  >
                    <option value="All">All Categories</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    title="Refresh"
                    onClick={() => {
                      refreshAll();
                      fetchTickets(currentPage, search);
                    }}
                    className="rounded-lg border border-border px-3 py-2 text-muted-foreground hover:bg-muted"
                  >
                    <Icon name="RefreshCw" size={17} />
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[1000px]">
                  <thead>
                    <tr className="border-b border-border bg-muted/20">
                      {[
                        "Ticket / Subject",
                        "Category",
                        "Requester",
                        "Department",
                        "Priority",
                        "Status",
                        "Created",
                        "Actions",
                      ].map((heading) => (
                        <th
                          key={heading}
                          className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={8} className="px-5 py-16 text-center">
                          <span className="text-sm text-muted-foreground">
                            Loading tickets...
                          </span>
                        </td>
                      </tr>
                    ) : filteredTickets.length ? (
                      filteredTickets.map((ticket) => (
                        <tr
                          key={ticket.id}
                          className="border-b border-border last:border-0 hover:bg-muted/20"
                        >
                          <td className="max-w-xs px-5 py-4">
                            <p className="font-mono text-xs font-semibold text-primary">
                              {ticket.ticket_number || `#${ticket.id}`}
                            </p>
                            <p className="mt-1 text-sm font-medium text-foreground">
                              {ticket.subject}
                            </p>
                          </td>

                          <td className="px-5 py-4 text-sm">
                            {ticket.category || "Other"}
                          </td>

                          <td className="px-5 py-4 text-sm text-foreground">
                            {ticket.requesterName}
                          </td>

                          <td className="px-5 py-4 text-sm text-foreground">
                            {ticket.departmentName}
                          </td>

                          <td className="px-5 py-4">
                            <span
                              className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${priorityClass(ticket.priority)}`}
                            >
                              {ticket.priority}
                            </span>
                          </td>

                          <td className="px-5 py-4">
                            <span
                              className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClass(ticket.status)}`}
                            >
                              {ticket.status}
                            </span>
                          </td>

                          <td className="whitespace-nowrap px-5 py-4 text-xs text-muted-foreground">
                            {formatDate(ticket.created_at)}
                          </td>

                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                iconName="Eye"
                                onClick={() => setActiveTicket(ticket)}
                              >
                                View
                              </Button>
                              <button
                                type="button"
                                title="Edit ticket"
                                onClick={() => openEditModal(ticket)}
                                className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-muted"
                              >
                                <Icon name="Pencil" size={15} />
                              </button>
                              <button
                                type="button"
                                title="Delete ticket"
                                onClick={() => setDeleteTarget(ticket)}
                                className="rounded-lg border border-border p-2 text-error hover:bg-error/10"
                              >
                                <Icon name="Trash2" size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="px-5 py-16 text-center">
                          <Icon
                            name="SearchX"
                            size={30}
                            className="mx-auto text-muted-foreground"
                          />
                          <h3 className="mt-4 font-semibold text-foreground">
                            No tickets found
                          </h3>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Create a ticket or change your filters.
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4 text-sm text-muted-foreground">
                <span>
                  {pagination.total} tickets total
                </span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    disabled={currentPage <= 1 || loading}
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    className="rounded-lg border border-border px-3 py-1.5 disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span>
                    Page {pagination.current_page || currentPage} of{" "}
                    {pagination.last_page || 1}
                  </span>
                  <button
                    type="button"
                    disabled={
                      currentPage >= (pagination.last_page || 1) || loading
                    }
                    onClick={() =>
                      setCurrentPage((page) =>
                        Math.min(pagination.last_page || 1, page + 1),
                      )
                    }
                    className="rounded-lg border border-border px-3 py-1.5 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>

      {/* Ticket details drawer */}
      {activeTicket && (
        <div className="fixed inset-0 z-[100] flex">
          <button
            type="button"
            aria-label="Close ticket details"
            onClick={() => setActiveTicket(null)}
            className="absolute inset-0 bg-black/50"
          />

          <aside className="relative ml-auto flex h-full w-full max-w-2xl flex-col bg-background shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-border bg-card p-6">
              <div className="min-w-0">
                <p className="font-mono text-sm font-bold text-primary">
                  {activeTicket.ticket_number}
                </p>
                <h2 className="mt-2 break-words text-xl font-bold text-foreground">
                  {activeTicket.subject}
                </h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span
                    className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClass(activeTicket.status)}`}
                  >
                    {activeTicket.status}
                  </span>
                  <span
                    className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${priorityClass(activeTicket.priority)}`}
                  >
                    {activeTicket.priority}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setActiveTicket(null)}
                className="rounded-lg p-2 hover:bg-muted"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="flex-1 space-y-5 overflow-y-auto p-6">
              {error && (
                <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 rounded-xl border border-border bg-muted/30 p-4 sm:grid-cols-2">
                {[
                  ["Requester", activeTicket.requesterName],
                  ["Department", activeTicket.departmentName],
                  ["Assigned To", activeTicket.assigneeName],
                  ["Category", activeTicket.category || "Other"],
                  ["Created", formatDateTime(activeTicket.created_at)],
                  ["Resolved", formatDateTime(activeTicket.resolved_at)],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="mt-1 break-words text-sm font-semibold text-foreground">
                      {value}
                    </p>
                  </div>
                ))}
              </div>

              <section className="rounded-xl border border-border bg-card p-5">
                <h3 className="mb-2 text-sm font-semibold text-foreground">
                  Description
                </h3>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                  {activeTicket.description || "No description provided."}
                </p>
              </section>

              <section className="rounded-xl border border-border bg-card p-5">
                <h3 className="mb-3 text-sm font-semibold text-foreground">
                  Update Status
                </h3>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((status) => (
                    <button
                      key={status}
                      type="button"
                      disabled={saving || status === activeTicket.status}
                      onClick={() => changeStatus(activeTicket, status)}
                      className={`rounded-lg border px-3 py-2 text-sm disabled:opacity-40 ${status === activeTicket.status ? statusClass(status) : "border-border hover:bg-muted"}`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Status changes are saved using the existing Laravel update
                  endpoint.
                </p>
              </section>

              <Button
                variant="outline"
                onClick={() => openEditModal(activeTicket)}
              >
                Edit Ticket Details
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Create / edit ticket modal */}
      {showModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center overflow-y-auto p-3 md:p-6">
          <button
            type="button"
            aria-label="Close ticket form"
            onClick={() => !saving && setShowModal(false)}
            className="fixed inset-0 bg-black/50"
          />

          <section className="relative z-10 flex max-h-[95vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl">
            <div className="flex items-center justify-between gap-4 border-b border-border bg-card px-5 py-4 md:px-7">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {editingTicket ? "Edit Service Ticket" : "Create Service Ticket"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Fields match your existing ServiceDeskController validation.
                </p>
              </div>
              <button
                type="button"
                disabled={saving}
                onClick={() => setShowModal(false)}
                className="rounded-lg p-2 hover:bg-muted"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={saveTicket} className="flex min-h-0 flex-1 flex-col">
              <div className="space-y-5 overflow-y-auto p-5 md:p-7">
                {formError && (
                  <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error">
                    {formError}
                  </div>
                )}

                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Subject *
                  </label>
                  <input
                    required
                    maxLength={255}
                    value={form.subject}
                    onChange={(event) => updateField("subject", event.target.value)}
                    placeholder="Describe the issue or request"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Description
                  </label>
                  <textarea
                    rows={4}
                    value={form.description}
                    onChange={(event) =>
                      updateField("description", event.target.value)
                    }
                    placeholder="Provide details about the issue or request"
                    className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      Category
                    </label>
                    <select
                      value={form.category}
                      onChange={(event) =>
                        updateField("category", event.target.value)
                      }
                      className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                    >
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      Priority *
                    </label>
                    <select
                      required
                      value={form.priority}
                      onChange={(event) =>
                        updateField("priority", event.target.value)
                      }
                      className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                    >
                      {PRIORITIES.map((priority) => (
                        <option key={priority} value={priority}>
                          {priority}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {editingTicket && (
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      Status
                    </label>
                    <select
                      value={form.status}
                      onChange={(event) =>
                        updateField("status", event.target.value)
                      }
                      className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                    >
                      {STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      Requester *
                    </label>
                    <select
                      required
                      value={form.requester_id}
                      onChange={(event) =>
                        updateField("requester_id", event.target.value)
                      }
                      className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                    >
                      <option value="">Select requester</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                          {user.employee_id ? ` (${user.employee_id})` : ""}
                          {user.email ? ` — ${user.email}` : ""}
                        </option>
                      ))}
                    </select>
                    {!users.length && (
                      <p className="mt-1 text-xs text-warning">
                        No active users returned by /admin/users.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      Department
                    </label>
                    <select
                      value={form.department_id}
                      onChange={(event) =>
                        updateField("department_id", event.target.value)
                      }
                      className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                    >
                      <option value="">No department</option>
                      {departments.map((department) => (
                        <option key={department.id} value={department.id}>
                          {department.name}
                          {department.code ? ` (${department.code})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Assign To
                  </label>
                  <select
                    value={form.assigned_to}
                    onChange={(event) =>
                      updateField("assigned_to", event.target.value)
                    }
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                  >
                    <option value="">Unassigned</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                        {user.email ? ` — ${user.email}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-border bg-card px-5 py-4 md:px-7">
                <Button
                  type="button"
                  variant="outline"
                  disabled={saving}
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={saving}
                  iconName="Save"
                  iconPosition="left"
                >
                  {saving
                    ? "Saving..."
                    : editingTicket
                      ? "Save Changes"
                      : "Create Ticket"}
                </Button>
              </div>
            </form>
          </section>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Close confirmation"
            onClick={() => !saving && setDeleteTarget(null)}
            className="absolute inset-0 bg-black/50"
          />
          <section className="relative w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-foreground">
              Delete Service Ticket?
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Delete {deleteTarget.ticket_number || deleteTarget.subject}? This
              action cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={saving}
                onClick={deleteTicket}
              >
                {saving ? "Deleting..." : "Delete Ticket"}
              </Button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default ServiceDesk;